import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Paper,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Palette as PaletteIcon,
  Edit as EditIcon,
  Notifications as NotificationIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import BusinessService from '../services/businessService';
import ThemeService from '../services/themeService';
import InventoryService from '../services/inventoryService';
import PlateService from '../services/plateService';
import dishService from '../services/dishService';
import { Business, Theme, Inventory, Plate, Dish, Vendor } from '../types';
import { Order } from '../types/cart';
import { getOrderDisplayTitle } from '../utils/orderDisplay';
import BusinessManagementForm from '../components/BusinessManagementForm';
import ThemeManagement from '../components/ThemeManagement';
import BusinessSelector from '../components/BusinessSelector';
import ThemeCard from '../components/ThemeCard';
import InventoryManagementForm from '../components/InventoryManagementForm';
import InventoryCard from '../components/InventoryCard';
import InventoryImages from '../components/InventoryImages';
import PlateManagementForm from '../components/PlateManagementForm';
import PlateCard from '../components/PlateCard';
import DishManagementForm from '../components/DishManagementForm';
import DishCard from '../components/DishCard';
import orderService from '../services/orderService';
import notificationService from '../services/notificationService';
import chatService from '../services/chatService';
import { useNavigate, useLocation } from 'react-router-dom';
import VendorService from '../services/vendorService';
import AdjustOrderAmountDialog from '../components/AdjustOrderAmountDialog';
import ChaaviVendorDashboardShell, {
  CHA,
  ChaaviVendorNavKey,
} from '../components/ChaaviVendorDashboardShell';
import ChaaviVendorOverviewHome from '../components/ChaaviVendorOverviewHome';
import { ratingService } from '../services/ratingService';

/** Gold CTA: `#c0873a` fill, `#a87030` on hover (inventory add) */
const addInventoryButtonSx = {
  bgcolor: CHA.accent,
  color: '#fff',
  textTransform: 'none' as const,
  boxShadow: 'none',
  '&:hover': { bgcolor: CHA.accentHover, boxShadow: 'none' },
};

/** Edit-style CTA: `#a87030` fill, white label; hover lightens to `CHA.accent` */
const chaaviEditButtonSx = {
  bgcolor: CHA.accentHover,
  color: '#fff',
  textTransform: 'none' as const,
  boxShadow: 'none',
  '&:hover': { bgcolor: CHA.accent, color: '#fff', boxShadow: 'none' },
};

const VendorDashboard: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [inventory, setInventory] = useState<Inventory[]>([]);
  const [plates, setPlates] = useState<Plate[]>([]);
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [businessDishesCache, setBusinessDishesCache] = useState<Record<string, Dish[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState((location.state as any)?.activeTab ?? 0);
  const [businessFormOpen, setBusinessFormOpen] = useState(false);
  const [inventoryFormOpen, setInventoryFormOpen] = useState(false);
  const [editingInventory, setEditingInventory] = useState<Inventory | null>(null);
  const [inventoryImagesOpen, setInventoryImagesOpen] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState<Inventory | null>(null);
  const [inventoryRefreshTrigger, setInventoryRefreshTrigger] = useState(0);
  const [plateFormOpen, setPlateFormOpen] = useState(false);
  const [editingPlate, setEditingPlate] = useState<Plate | null>(null);
  const [dishFormOpen, setDishFormOpen] = useState(false);
  const [editingDish, setEditingDish] = useState<Dish | null>(null);
  const [vendorProfile, setVendorProfile] = useState<Vendor | null>(null);
  const [minOrderInput, setMinOrderInput] = useState('');
  const [savingMinOrder, setSavingMinOrder] = useState(false);
  const [minOrderMessage, setMinOrderMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isEditingMinOrder, setIsEditingMinOrder] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingBusiness, setDeletingBusiness] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Use ref to track if we've already fetched vendor data to prevent infinite loops
  const hasFetchedVendorDataRef = useRef(false);
  const lastUserPhoneRef = useRef<string | undefined>(undefined);
  
  const parsedMinOrderValue = parseFloat(minOrderInput);
  const isMinOrderInputValid = minOrderInput.trim() !== '' && !Number.isNaN(parsedMinOrderValue) && parsedMinOrderValue >= 0;
  const currentMinOrderAmount = vendorProfile?.minOrderAmount ?? 0;
  const canSaveMinOrder = isMinOrderInputValid && !savingMinOrder;
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
  
  // Order management state
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [orderToScrollTo, setOrderToScrollTo] = useState<string | null>(null);
  const [savedScrollPosition, setSavedScrollPosition] = useState<number | null>(null);
  const [adjustAmountDialogOpen, setAdjustAmountDialogOpen] = useState(false);
  const [selectedOrderForAdjustment, setSelectedOrderForAdjustment] = useState<Order | null>(null);
  const [isUpdatingAmount, setIsUpdatingAmount] = useState(false);
  
  // Notification state
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [vendorNotifications, setVendorNotifications] = useState<any[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [ratingAgg, setRatingAgg] = useState<{ avg: number; count: number } | null>(null);
  const [chaaviNav, setChaaviNav] = useState<ChaaviVendorNavKey>('dashboard');

  const fetchNotificationCount = useCallback(async () => {
    if (!user?.phoneNumber) return;
    
    try {
      const count = await notificationService.getNotificationCount(user.phoneNumber);
      setUnreadNotificationCount(count);
    } catch (err: any) {
      console.error('Error fetching notification count:', err);
    }
  }, [user?.phoneNumber]);

  const fetchVendorNotifications = useCallback(async () => {
    if (!user?.phoneNumber) return;
    
    try {
      setLoadingNotifications(true);
      const notifications = await notificationService.getNotificationsByVendor(user.phoneNumber);
      // Sort by date, newest first
      const sortedNotifications = notifications.sort((a: any, b: any) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setVendorNotifications(sortedNotifications);
      setUnreadNotificationCount(notifications.filter((n: any) => !n.isRead).length);
    } catch (err: any) {
      console.error('Error fetching vendor notifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  }, [user?.phoneNumber]);

  const fetchUnreadChatCount = useCallback(async () => {
    if (!user?.phoneNumber) return;
    
    try {
      const count = await chatService.getTotalUnreadCount(user.phoneNumber);
      setUnreadChatCount(count);
    } catch (err: any) {
      console.error('Error fetching unread chat count:', err);
    }
  }, [user?.phoneNumber]);

  const fetchOrdersForBusiness = useCallback(async () => {
    if (!selectedBusiness) return;
    
    try {
      setOrdersLoading(true);
      setOrdersError(null);
      console.log('Fetching orders for business:', selectedBusiness.businessId);
      const ordersData = await orderService.getOrdersByBusinessId(selectedBusiness.businessId);
      console.log('Orders fetched:', ordersData);
      setOrders(ordersData);
    } catch (err: any) {
      console.error('Error fetching orders:', err);
      setOrdersError(err.message || 'Failed to fetch orders');
    } finally {
      setOrdersLoading(false);
    }
  }, [selectedBusiness]);

  // Update activeTab when location state changes and fetch data
  useEffect(() => {
    const stateActiveTab = (location.state as any)?.activeTab;
    console.log('Location state changed:', location.state, 'activeTab from state:', stateActiveTab, 'Current activeTab:', activeTab);
    if (stateActiveTab !== undefined && stateActiveTab !== activeTab) {
      console.log('Updating activeTab from location state:', stateActiveTab, 'Current activeTab:', activeTab);
      setActiveTab(stateActiveTab);
      
      // Fetch data when navigating to Orders or Notifications tab from location state
      if (selectedBusiness) {
        const ordersTabIndex = getTabIndexForBusiness('Orders', selectedBusiness);
        const notificationsTabIndex = getTabIndexForBusiness('Notifications', selectedBusiness);
        if (stateActiveTab === ordersTabIndex) {
          fetchOrdersForBusiness();
        }
        if (stateActiveTab === notificationsTabIndex) {
          fetchNotificationCount();
          fetchVendorNotifications();
        }
      }
    }
    // getTabIndexForBusiness is stable enough in practice; listing it would retrigger every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, selectedBusiness, activeTab, fetchOrdersForBusiness, fetchNotificationCount, fetchVendorNotifications]);
  
  useEffect(() => {
    const loadVendorProfile = async () => {
      if (!user?.phoneNumber) {
        return;
      }
      
      try {
        const vendorData = await VendorService.getVendorByPhoneNumber(user.phoneNumber);
        setVendorProfile(vendorData);
        if (vendorData && typeof vendorData.minOrderAmount === 'number') {
          setMinOrderInput(vendorData.minOrderAmount.toString());
        } else {
          setMinOrderInput('0');
        }
      } catch (err) {
        console.error('Error fetching vendor profile:', err);
      }
    };
    
    loadVendorProfile();
  }, [user]);

  const handleMinOrderSave = async () => {
    if (!user?.phoneNumber) {
      return;
    }
    
    if (!isMinOrderInputValid) {
      setMinOrderMessage({ type: 'error', text: 'Enter a non-negative amount.' });
      return;
    }
    
    const normalizedAmount = Number(parsedMinOrderValue.toFixed(2));
    
    try {
      setSavingMinOrder(true);
      setMinOrderMessage(null);
      
      let updatedVendor: Vendor;
      
      // Try to update existing vendor
      try {
        updatedVendor = await VendorService.updateVendor(user.phoneNumber, { minOrderAmount: normalizedAmount });
      } catch (updateErr: any) {
        // If vendor doesn't exist (404), create it
        if (updateErr?.response?.status === 404) {
          // Use business data if available, otherwise use user data
          const firstBusiness = businesses.length > 0 ? businesses[0] : null;
          
          if (!firstBusiness) {
            throw new Error('Please create a business profile first before setting minimum order amount.');
          }
          
          // Create vendor with business data and minOrderAmount
          updatedVendor = await VendorService.createVendor({
            phoneNumber: user.phoneNumber,
            businessName: firstBusiness.businessName,
            businessDescription: firstBusiness.businessDescription || '',
            businessAddress: firstBusiness.businessAddress,
            businessPhone: firstBusiness.businessPhone,
            businessEmail: firstBusiness.businessEmail,
            minOrderAmount: normalizedAmount,
          });
        } else {
          throw updateErr;
        }
      }
      
      setVendorProfile(updatedVendor);
      setMinOrderMessage({ type: 'success', text: 'Minimum order amount saved successfully.' });
      setBusinesses(prev => prev.map(b => ({ ...b, minOrderAmount: normalizedAmount })));
      setSelectedBusiness(prev => (prev ? { ...prev, minOrderAmount: normalizedAmount } : prev));
      setIsEditingMinOrder(false); // Exit edit mode after successful save
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to save minimum order amount.';
      setMinOrderMessage({ type: 'error', text: message });
    } finally {
      setSavingMinOrder(false);
    }
  };

  const handleMinOrderEdit = () => {
    setIsEditingMinOrder(true);
    setMinOrderMessage(null);
    // Set input to current value when entering edit mode
    if (currentMinOrderAmount > 0) {
      setMinOrderInput(currentMinOrderAmount.toString());
    }
  };

  const handleMinOrderCancel = () => {
    setIsEditingMinOrder(false);
    setMinOrderMessage(null);
    // Reset input to current saved value
    if (currentMinOrderAmount > 0) {
      setMinOrderInput(currentMinOrderAmount.toString());
    } else {
      setMinOrderInput('');
    }
  };


  useEffect(() => {
    const fetchVendorData = async () => {
      // Wait for user to be available
      if (!user) {
        setLoading(true);
        return;
      }

      if (!user.phoneNumber) {
        setError('User phone number not available');
        setLoading(false);
        return;
      }
      
      // Reset fetch flag if user phone number changed
      if (lastUserPhoneRef.current !== user.phoneNumber) {
        hasFetchedVendorDataRef.current = false;
        lastUserPhoneRef.current = user.phoneNumber;
      }
      
      // Prevent multiple simultaneous fetches
      if (hasFetchedVendorDataRef.current) {
        return;
      }

      try {
        hasFetchedVendorDataRef.current = true;
        setLoading(true);
        setError(null);
        
        // Get all businesses for this vendor
        const businessesData = await BusinessService.getBusinessesByVendorPhoneNumber(user.phoneNumber);
        setBusinesses(businessesData);
        
        // Auto-select first business if available
        if (businessesData.length > 0) {
          const business = businessesData[0];
          console.log('🔍 Selected business:', business.businessId, 'Category:', business.businessCategory);
          setSelectedBusiness(business);
          
          // Fetch data based on business category
          if (business.businessCategory === 'caters') {
            // For catering businesses, fetch plates and dishes
            try {
              const [platesData, dishesData] = await Promise.all([
                PlateService.getPlatesByBusinessId(business.businessId),
                dishService.getDishesByBusinessId(business.businessId).catch(err => {
                  console.error('❌ Error fetching dishes for business:', business.businessId, err);
                  // On initial load, don't use cache (it's empty anyway), return empty array
                  return [];
                })
              ]);
              
              setPlates(platesData || []);
              const dishesArray = Array.isArray(dishesData) ? dishesData : [];
              setDishes(dishesArray);
              
              // Always update cache with fetched data (even if empty)
              setBusinessDishesCache((prev) => {
                const updated = {
                  ...prev,
                  [business.businessId]: dishesArray,
                };
                return updated;
              });
              setThemes([]);
              setInventory([]);
            } catch (err: any) {
              console.error('Error fetching plates/dishes:', err);
              // Set empty arrays but don't fail the entire operation
              setPlates([]);
              // On initial load, cache is empty, so just set empty array
              setDishes([]);
              setThemes([]);
              setInventory([]);
            }
          } else {
            // For non-catering businesses, fetch themes and inventory (skip inventory for farmhouse)
            try {
              const themesData = await ThemeService.getThemesByBusinessId(business.businessId);
              setThemes(themesData);
              
              if (isFarmhouseBusiness(business)) {
                setInventory([]);
              } else {
                const inventoryData = await InventoryService.getInventoryByBusinessId(business.businessId);
                setInventory(inventoryData);
              }
              
              setPlates([]);
              const cachedDishes = businessDishesCache[business.businessId] || [];
              setDishes(cachedDishes);
            } catch (err: any) {
              console.error('Error fetching themes/inventory:', err);
              // Set empty arrays but don't fail the entire operation
              setThemes([]);
              setInventory([]);
              setPlates([]);
              setDishes([]);
            }
          }
        } else {
          setSelectedBusiness(null);
          setThemes([]);
          setInventory([]);
          setPlates([]);
          setDishes([]);
        }
        
      } catch (err: any) {
        if (err.response?.status === 404) {
          // 404 is expected for new vendors - no business profile exists yet
          setBusinesses([]);
          setSelectedBusiness(null);
          setThemes([]);
        } else if (err.response?.status === 403) {
          setError('Access denied. Please ensure you are logged in and have the correct permissions.');
        } else {
          setError('Failed to fetch business data. Please try again later.');
        }
        console.error('Error fetching vendor data:', err);
        hasFetchedVendorDataRef.current = false; // Reset on error to allow retry
      } finally {
        setLoading(false);
      }
    };

    fetchVendorData();
    
    // Fetch notification count and notifications
    if (user?.phoneNumber) {
      fetchNotificationCount();
      fetchVendorNotifications(); // Also fetch notifications on load
      fetchUnreadChatCount();
    }
    // businessDishesCache is intentionally excluded to prevent infinite loops
    // The cache is only used for reading, not as a trigger for refetching
  }, [user, user?.phoneNumber, fetchNotificationCount, fetchVendorNotifications, fetchUnreadChatCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh unread counts when component mounts or user changes
  useEffect(() => {
    if (user?.phoneNumber) {
      const interval = setInterval(() => {
        fetchNotificationCount();
        fetchUnreadChatCount();
      }, 30000); // Refresh every 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [user?.phoneNumber, fetchNotificationCount, fetchUnreadChatCount]);

  useEffect(() => {
    if (!selectedBusiness?.businessId) {
      setRatingAgg(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await ratingService.getRatingsByBusiness(selectedBusiness.businessId);
        if (cancelled) return;
        if (!list.length) {
          setRatingAgg({ avg: 0, count: 0 });
        } else {
          const sum = list.reduce((s, r) => s + r.rating, 0);
          setRatingAgg({ avg: sum / list.length, count: list.length });
        }
      } catch {
        if (!cancelled) setRatingAgg(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBusiness?.businessId]);

  useEffect(() => {
    if (!selectedBusiness?.businessId) return;
    void fetchOrdersForBusiness();
  }, [selectedBusiness?.businessId, fetchOrdersForBusiness]);

  const handleBusinessSelect = useCallback(async (business: Business) => {
    setSelectedBusiness(business);
    try {
      // Fetch data based on business category
      if (isCateringBusiness(business)) {
        // For catering businesses, fetch plates and dishes
        try {
          const [platesData, dishesData] = await Promise.all([
            PlateService.getPlatesByBusinessId(business.businessId),
            dishService.getDishesByBusinessId(business.businessId).catch(err => {
              console.warn('Error fetching dishes for business:', business.businessId, err);
              // Return cached dishes if available, otherwise empty array
              return businessDishesCache[business.businessId] || [];
            })
          ]);
          console.log('handleBusinessSelect - Fetched dishes for business:', business.businessId, 'Count:', dishesData?.length || 0, dishesData);
          setPlates(platesData);
          setDishes(dishesData || []);
          // Always update cache with fetched data (even if empty, to prevent stale cache)
          setBusinessDishesCache((prev) => ({
            ...prev,
            [business.businessId]: dishesData || [],
          }));
          setThemes([]);
          setInventory([]);
        } catch (err) {
          console.error('Error fetching plates/dishes:', err);
          // Try to use cached data as fallback
          const cachedDishes = businessDishesCache[business.businessId] || [];
          setDishes(cachedDishes);
          setPlates([]);
          setThemes([]);
          setInventory([]);
        }
      } else {
        // For non-catering businesses, fetch themes and inventory (skip inventory for farmhouse)
        const themesData = await ThemeService.getThemesByBusinessId(business.businessId);
        setThemes(themesData);
        if (isFarmhouseBusiness(business)) {
          setInventory([]);
        } else {
          const inventoryData = await InventoryService.getInventoryByBusinessId(business.businessId);
          setInventory(inventoryData);
        }
        setPlates([]);
        setDishes([]);
      }
    } catch (err) {
      console.error('Error fetching business data:', err);
      setThemes([]);
      setInventory([]);
      setPlates([]);
      // Try to use cached dishes if available
      const cachedDishes = businessDishesCache[business.businessId] || [];
      setDishes(cachedDishes);
    }
  }, [businessDishesCache]);

  const handleBusinessesChange = useCallback((updatedBusinesses: Business[]) => {
    setBusinesses(updatedBusinesses);
  }, []);

  const handleDeleteBusiness = async () => {
    if (!selectedBusiness || !user?.phoneNumber) {
      return;
    }
    
    try {
      setDeletingBusiness(true);
      await BusinessService.deleteBusiness(selectedBusiness.businessId, user.phoneNumber);
      
      // Remove the deleted business from the list
      const updatedBusinesses = businesses.filter(b => b.businessId !== selectedBusiness.businessId);
      setBusinesses(updatedBusinesses);
      
      // Select the first remaining business, or set to null if none
      if (updatedBusinesses.length > 0) {
        setSelectedBusiness(updatedBusinesses[0]);
      } else {
        setSelectedBusiness(null);
      }
      
      setDeleteDialogOpen(false);
      alert('Business and all related data deleted successfully.');
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.message || 'Failed to delete business.';
      alert(errorMessage);
    } finally {
      setDeletingBusiness(false);
    }
  };


  const handleBusinessSuccess = async (updatedBusiness: Business) => {
    setBusinessFormOpen(false);
    
    // Update businesses list
    const updatedBusinesses = businesses.some(b => b.businessId === updatedBusiness.businessId)
      ? businesses.map(b => b.businessId === updatedBusiness.businessId ? updatedBusiness : b)
      : [...businesses, updatedBusiness];
    
    setBusinesses(updatedBusinesses);
    
    // If this was a new business or the selected business was updated, select it
    if (!selectedBusiness || selectedBusiness.businessId === updatedBusiness.businessId) {
      setSelectedBusiness(updatedBusiness);
      try {
        // Fetch data based on business category
        if (isCateringBusiness(updatedBusiness)) {
          // For catering businesses, only fetch plates
          const platesData = await PlateService.getPlatesByBusinessId(updatedBusiness.businessId);
          setPlates(platesData);
          setThemes([]);
          setInventory([]);
        } else {
          // For non-catering businesses, fetch themes and inventory (skip inventory for farmhouse)
          const themesData = await ThemeService.getThemesByBusinessId(updatedBusiness.businessId);
          setThemes(themesData);
          if (isFarmhouseBusiness(updatedBusiness)) {
            setInventory([]);
          } else {
            const inventoryData = await InventoryService.getInventoryByBusinessId(updatedBusiness.businessId);
            setInventory(inventoryData);
          }
          setPlates([]);
        }
      } catch (err) {
        console.error('Error fetching data after business update:', err);
        setThemes([]);
        setInventory([]);
        setPlates([]);
      }
    }
  };

  const handleThemesChange = (updatedThemes: Theme[]) => {
    setThemes(updatedThemes);
  };

  const handleInventorySuccess = async () => {
    setInventoryFormOpen(false);
    setEditingInventory(null);
    
    if (selectedBusiness && !isFarmhouseBusiness(selectedBusiness)) {
      try {
        const inventoryData = await InventoryService.getInventoryByBusinessId(selectedBusiness.businessId);
        setInventory(inventoryData);
      } catch (err) {
        console.error('Error fetching inventory after update:', err);
      }
    }
  };

  const handleEditInventory = (inventory: Inventory) => {
    setEditingInventory(inventory);
    setInventoryFormOpen(true);
  };

  const handleDeleteInventory = async (inventoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this inventory item?')) {
      return;
    }

    try {
      await InventoryService.deleteInventory(inventoryId, user?.phoneNumber);
      const updatedInventory = inventory.filter(item => item.inventoryId !== inventoryId);
      setInventory(updatedInventory);
    } catch (err: any) {
      console.error('Error deleting inventory:', err);
      alert(err.message || 'Failed to delete inventory item');
    }
  };

  const handleViewInventoryImages = (inventory: Inventory) => {
    setSelectedInventory(inventory);
    setInventoryImagesOpen(true);
  };

  const handleInventoryImagesClose = async () => {
    setInventoryImagesOpen(false);
    setSelectedInventory(null);
    
    // Refresh inventory data to show updated images
    if (selectedBusiness && !isFarmhouseBusiness(selectedBusiness)) {
      try {
        const inventoryData = await InventoryService.getInventoryByBusinessId(selectedBusiness.businessId);
        setInventory(inventoryData);
        // Increment refresh trigger to force InventoryCard to re-fetch images
        setInventoryRefreshTrigger(prev => prev + 1);
      } catch (err) {
        console.error('Error refreshing inventory after image update:', err);
      }
    }
  };

  const handlePriceUpdate = async (inventoryId: string, newPrice: number) => {
    // Update the local inventory state
    const updatedInventory = inventory.map(item => 
      item.inventoryId === inventoryId 
        ? { ...item, price: newPrice }
        : item
    );
    setInventory(updatedInventory);
  };

  // Plate management handlers
  const handleAddPlate = () => {
    setEditingPlate(null);
    setPlateFormOpen(true);
  };

  const handleEditPlate = (plate: Plate) => {
    setEditingPlate(plate);
    setPlateFormOpen(true);
  };

  const handlePlateSuccess = async (updatedPlate: Plate) => {
    setPlateFormOpen(false);
    setEditingPlate(null);
    
    // Update plates list
    const updatedPlates = plates.some(p => p.plateId === updatedPlate.plateId)
      ? plates.map(p => p.plateId === updatedPlate.plateId ? updatedPlate : p)
      : [...plates, updatedPlate];
    
    setPlates(updatedPlates);
  };

  const handlePlateDelete = (plateId: string) => {
    setPlates(plates.filter(p => p.plateId !== plateId));
  };

  const handlePlateUpdate = (updatedPlate: Plate) => {
    setPlates(plates.map(p => p.plateId === updatedPlate.plateId ? updatedPlate : p));
  };

  const handleAddDish = () => {
    setEditingDish(null);
    setDishFormOpen(true);
  };

  const handleEditDish = (dish: Dish) => {
    setEditingDish(dish);
    setDishFormOpen(true);
  };

  const handleDishSuccess = async (updatedDish: Dish) => {
    setDishFormOpen(false);
    setEditingDish(null);
    
    // Update dishes list
    const updatedDishes = dishes.some(d => d.dishId === updatedDish.dishId)
      ? dishes.map(d => d.dishId === updatedDish.dishId ? updatedDish : d)
      : [...dishes, updatedDish];
    
    setDishes(updatedDishes);
    if (selectedBusiness) {
      setBusinessDishesCache(prev => ({
        ...prev,
        [selectedBusiness.businessId]: updatedDishes,
      }));
    }
  };

  const handleDishDelete = (dishId: string) => {
    const updatedDishes = dishes.filter(d => d.dishId !== dishId);
    setDishes(updatedDishes);
    if (selectedBusiness) {
      setBusinessDishesCache(prev => ({
        ...prev,
        [selectedBusiness.businessId]: updatedDishes,
      }));
    }
  };

  const handleDishUpdate = (updatedDish: Dish) => {
    const updatedDishes = dishes.map(d => d.dishId === updatedDish.dishId ? updatedDish : d);
    setDishes(updatedDishes);
    if (selectedBusiness) {
      setBusinessDishesCache(prev => ({
        ...prev,
        [selectedBusiness.businessId]: updatedDishes,
      }));
    }
  };


  const handleMarkNotificationAsRead = async (notificationId: number) => {
    try {
      await notificationService.markAsRead(notificationId);
      // Update local state
      setVendorNotifications(prev => 
        prev.map(n => n.notificationId === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadNotificationCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      console.error('Error marking notification as read:', err);
    }
  };


  // Helper function to check if business is tent type
  const isTentBusiness = (business: Business | null): boolean => {
    if (!business) return false;
    const category = business.businessCategory?.toLowerCase() || '';
    // More specific check - only match exact tent categories
    return category === 'tent_house' || category === 'tent' || 
           (category.includes('tent') && !category.includes('cater'));
  };
  
  const isCateringBusiness = (business: Business | null): boolean => {
    return business?.businessCategory === 'caters';
  };
  
  const isFarmhouseBusiness = (business: Business | null): boolean => {
    if (!business) return false;
    const category = business.businessCategory?.toLowerCase() || '';
    return category.includes('farmhouse') || category.includes('farm house');
  };
  
  const getTabLabelsForBusiness = (business: Business | null): string[] => {
    const tabLabels: string[] = ['Overview'];
    if (isTentBusiness(business)) {
      tabLabels.push('Theme', 'Inventory', 'Orders', 'Notifications', 'Explore');
      return tabLabels;
    }
    if (isCateringBusiness(business)) {
      tabLabels.push('Plate', 'Dish', 'Orders', 'Notifications', 'Explore');
      return tabLabels;
    }
    tabLabels.push('Themes');
    if (!isFarmhouseBusiness(business)) {
      tabLabels.push('Inventory');
    }
    tabLabels.push('Orders', 'Notifications', 'Explore');
    return tabLabels;
  };
  
  const getTabIndexForBusiness = (label: string, business: Business | null): number => {
    const labels = getTabLabelsForBusiness(business);
    return labels.indexOf(label);
  };

  const handleChaaviNav = (key: ChaaviVendorNavKey) => {
    setChaaviNav(key);
    switch (key) {
      case 'dashboard':
        navigate('/vendor-dashboard');
        setActiveTab(0);
        break;
      case 'business':
        setActiveTab(0);
        window.setTimeout(() => {
          document.getElementById('vendor-business-selector')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 80);
        break;
      case 'availability':
        navigate('/availability');
        break;
      case 'messages':
        navigate('/vendor-chat');
        break;
      case 'reviews':
        setActiveTab(0);
        setSnackbar({
          open: true,
          message: 'Your average rating and review totals are in the KPI row above.',
          severity: 'success',
        });
        break;
      case 'analytics':
        setSnackbar({
          open: true,
          message: 'Analytics will be available in a future update.',
          severity: 'info',
        });
        break;
      default:
        break;
    }
  };

  const handleChaaviOpenNotifications = () => {
    if (!selectedBusiness) {
      navigate('/vendor-orders-notifications');
      return;
    }
    const idx = getTabIndexForBusiness('Notifications', selectedBusiness);
    if (idx >= 0) {
      setActiveTab(idx);
      fetchVendorNotifications();
    } else {
      navigate('/vendor-orders-notifications');
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    console.log('🔵 ========== TAB CLICKED ==========');
    console.log('🔵 newValue (tab index):', newValue);
    console.log('🔵 Business category:', selectedBusiness?.businessCategory);
    console.log('🔵 Is tent business:', isTentBusiness(selectedBusiness));
    console.log('🔵 Current activeTab:', activeTab);
    
    // Material-UI Tabs use the index based on the order of tabs in JSX
    // We need to calculate the actual tab indices based on what's rendered
    
    // Calculate which tabs are actually rendered
    const tabLabels = getTabLabelsForBusiness(selectedBusiness);
    
    console.log('🔵 Tab labels in order:', tabLabels);
    console.log('🔵 Total tabs:', tabLabels.length);
    
    // Safety check - make sure newValue is within bounds
    if (newValue < 0 || newValue >= tabLabels.length) {
      console.error('❌ Invalid tab index:', newValue, 'Max index:', tabLabels.length - 1);
      return;
    }
    
    const clickedTabLabel = tabLabels[newValue];
    console.log('🔵 Clicked tab label:', clickedTabLabel);
    
    // If it's the Explore tab, navigate to explore page IMMEDIATELY and don't update state
    if (clickedTabLabel === 'Explore') {
      console.log('🔵 Navigating to Explore page...');
      navigate('/explore');
      return; // Don't update activeTab or fetch data
    }
    
    // Update active tab state
    console.log('🔵 Setting activeTab to:', newValue);
    setActiveTab(newValue);
    
    // Determine which tab was clicked
    const isOrdersTab = clickedTabLabel === 'Orders';
    const isNotificationsTab = clickedTabLabel === 'Notifications';
    
    console.log('🔵 Tab analysis:', {
      newValue,
      clickedTabLabel,
      businessCategory: selectedBusiness?.businessCategory,
      isOrdersTab,
      isNotificationsTab,
      isTent: isTentBusiness(selectedBusiness)
    });
    
    if (isOrdersTab) {
      fetchOrdersForBusiness();
    }
    
    if (isNotificationsTab) {
      fetchNotificationCount();
      fetchVendorNotifications();
    }
    
    console.log('🔵 ========== TAB CHANGE COMPLETE ==========');
    console.log('🔵 Final activeTab value:', newValue);
  };

  // Scroll to the order after orders are updated
  useEffect(() => {
    if (orderToScrollTo && orders.length > 0 && !ordersLoading) {
      // Immediately restore saved position to prevent jump to top
      if (savedScrollPosition !== null) {
        window.scrollTo({ top: savedScrollPosition - 100, behavior: 'auto' });
      }
      
      // Then try to find and scroll to the element with multiple attempts
      const scrollToOrder = () => {
        const orderElement = document.getElementById(`order-${orderToScrollTo}`);
        if (orderElement) {
          // Calculate absolute position correctly
          const rect = orderElement.getBoundingClientRect();
          const absoluteElementTop = rect.top + window.pageYOffset;
          
          // Scroll to the element
          window.scrollTo({ 
            top: absoluteElementTop - 100,
            behavior: 'auto'
          });
          
          // Clear the scroll tracking
          setOrderToScrollTo(null);
          setSavedScrollPosition(null);
        } else if (savedScrollPosition !== null) {
          // If element not found, restore saved position
          window.scrollTo({ top: savedScrollPosition - 100, behavior: 'auto' });
          setOrderToScrollTo(null);
          setSavedScrollPosition(null);
        }
      };
      
      // Try multiple times with increasing delays to ensure DOM is ready
      setTimeout(() => scrollToOrder(), 0);
      setTimeout(() => scrollToOrder(), 50);
      setTimeout(() => scrollToOrder(), 100);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToOrder();
        });
      });
    }
  }, [orders, ordersLoading, orderToScrollTo, savedScrollPosition]);

  const handleOrderStatusUpdate = async (orderId: string, newStatus: Order['status']) => {
    try {
      // Get the order element's position BEFORE updating
      const orderElement = document.getElementById(`order-${orderId}`);
      let elementPosition: number | null = null;
      
      if (orderElement) {
        const rect = orderElement.getBoundingClientRect();
        elementPosition = rect.top + window.pageYOffset;
      }
      
      // Save both scroll position and element position
      const currentScrollPosition = window.scrollY;
      setSavedScrollPosition(elementPosition !== null ? elementPosition : currentScrollPosition);
      
      // Store the order ID to scroll to after refresh
      setOrderToScrollTo(orderId);
      await orderService.updateOrderStatus(orderId, newStatus);
      // Refresh orders after status update
      await fetchOrdersForBusiness();
      // The useEffect will handle scrolling to the order after orders are loaded
    } catch (err: any) {
      console.error('Error updating order status:', err);
      setOrdersError(err.message || 'Failed to update order status');
      setOrderToScrollTo(null); // Clear on error
      setSavedScrollPosition(null);
    }
  };

  const handleConfirmOrderClick = (order: Order) => {
    // Open adjustment dialog for pending orders
    if (order.status === 'PENDING') {
      setSelectedOrderForAdjustment(order);
      setAdjustAmountDialogOpen(true);
    } else {
      // For other statuses, directly update
      handleOrderStatusUpdate(order.orderId.toString(), 'CONFIRMED');
    }
  };

  const handleAdjustAmountConfirm = async (newAmount: number) => {
    if (!selectedOrderForAdjustment) return;

    try {
      setIsUpdatingAmount(true);
      setOrdersError(null);

      // Get the order element's position BEFORE updating
      const orderElement = document.getElementById(`order-${selectedOrderForAdjustment.orderId}`);
      let elementPosition: number | null = null;
      
      if (orderElement) {
        const rect = orderElement.getBoundingClientRect();
        elementPosition = rect.top + window.pageYOffset;
      }
      
      const currentScrollPosition = window.scrollY;
      setSavedScrollPosition(elementPosition !== null ? elementPosition : currentScrollPosition);
      setOrderToScrollTo(selectedOrderForAdjustment.orderId.toString());

      // Update the order amount if it has changed
      if (Math.abs(newAmount - selectedOrderForAdjustment.totalAmount) > 0.01) {
        await orderService.updateOrderAmount(selectedOrderForAdjustment.orderId.toString(), newAmount);
      }

      // Then confirm the order
      await orderService.updateOrderStatus(selectedOrderForAdjustment.orderId.toString(), 'CONFIRMED');
      
      // Refresh orders
      await fetchOrdersForBusiness();

      // Close dialog
      setAdjustAmountDialogOpen(false);
      setSelectedOrderForAdjustment(null);

      // Show success message
      setSnackbar({
        open: true,
        message: 'Order placed successfully!',
        severity: 'success'
      });
    } catch (err: any) {
      console.error('Error adjusting amount and confirming order:', err);
      setOrdersError(err.message || 'Failed to adjust amount and confirm order');
      setSnackbar({
        open: true,
        message: err.message || 'Failed to adjust amount and confirm order',
        severity: 'error'
      });
    } finally {
      setIsUpdatingAmount(false);
    }
  };

  const handleAdjustAmountCancel = () => {
    setAdjustAmountDialogOpen(false);
    setSelectedOrderForAdjustment(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (businesses.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              My Business Dashboard
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Welcome back, {user?.firstName}! Here's your business overview.
            </Typography>
          </Box>
        </Box>
        
        <BusinessSelector
          userPhoneNumber={user?.phoneNumber || ''}
          selectedBusiness={selectedBusiness}
          onBusinessSelect={handleBusinessSelect}
          onBusinessesChange={handleBusinessesChange}
        />
        
      </Box>
    );
  }

  // Show loading if auth is still loading or if we're waiting for user data
  if (authLoading || (!user && loading)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  // If user is not available after loading, show error
  if (!user) {
    return (
      <Box>
        <Alert severity="error">
          Unable to load user information. Please try logging in again.
        </Alert>
      </Box>
    );
  }

  const exploreTabIndex = selectedBusiness
    ? getTabIndexForBusiness('Explore', selectedBusiness)
    : -1;

  return (
    <ChaaviVendorDashboardShell
      userFirstName={user?.firstName || 'Vendor'}
      footerLabel={
        selectedBusiness
          ? String(selectedBusiness.businessCategory || 'Vendor').replace(/_/g, ' ')
          : 'Vendor'
      }
      unreadChatCount={unreadChatCount}
      unreadNotificationCount={unreadNotificationCount}
      onOpenNotifications={handleChaaviOpenNotifications}
      onAddBusiness={() => setBusinessFormOpen(true)}
      activeNav={chaaviNav}
      onNav={handleChaaviNav}
    >
      <Box id="vendor-business-selector" sx={{ mb: 3 }}>
        <BusinessSelector
          userPhoneNumber={user?.phoneNumber || ''}
          selectedBusiness={selectedBusiness}
          onBusinessSelect={handleBusinessSelect}
          onBusinessesChange={handleBusinessesChange}
          hideAddBusinessButton
        />
      </Box>

      {selectedBusiness && (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, position: 'relative', zIndex: 1 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => {
                if (newValue !== activeTab) {
                  handleTabChange(e, newValue);
                } else {
                  // Force update even if value is the same
                  handleTabChange(e, newValue);
                }
              }}
              aria-label="vendor dashboard tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ 
                '& .MuiTab-root': {
                  pointerEvents: 'auto !important',
                  cursor: 'pointer !important',
                  minHeight: '48px',
                  userSelect: 'none',
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: CHA.accentHover,
                },
                '& .MuiTab-root.Mui-selected': {
                  color: CHA.accentHover,
                },
                '& .MuiTabScrollButton-root': {
                  color: CHA.accentHover,
                },
              }}
            >
              <Tab label="Overview" />
              {isTentBusiness(selectedBusiness) && <Tab label="Theme" />}
              {isTentBusiness(selectedBusiness) && <Tab label="Inventory" />}
              {!isTentBusiness(selectedBusiness) && !isCateringBusiness(selectedBusiness) && <Tab label="Themes" />}
              {!isTentBusiness(selectedBusiness) && !isCateringBusiness(selectedBusiness) && !isFarmhouseBusiness(selectedBusiness) && (
                <Tab label="Inventory" />
              )}
              {!isTentBusiness(selectedBusiness) && isCateringBusiness(selectedBusiness) && <Tab label="Plate" />}
              {!isTentBusiness(selectedBusiness) && isCateringBusiness(selectedBusiness) && <Tab label="Dish" />}
              {/* Orders Tab - For tent businesses and non-tent businesses */}
              {(isTentBusiness(selectedBusiness) || !isTentBusiness(selectedBusiness)) && (
                <Tab 
                  label="Orders"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('🟡 Orders Tab onClick fired directly!');
                    console.log('🟡 Current activeTab:', activeTab);
                    console.log('🟡 selectedBusiness:', selectedBusiness?.businessName);
                    console.log('🟡 isTentBusiness:', isTentBusiness(selectedBusiness));
                    // Calculate correct tab index based on business type
                    const ordersTabIndex = getTabIndexForBusiness('Orders', selectedBusiness);
                    if (ordersTabIndex === -1) {
                      return;
                    }
                    console.log('🟡 Setting activeTab to:', ordersTabIndex);
                    setActiveTab(ordersTabIndex);
                    fetchOrdersForBusiness();
                  }}
                />
              )}
              {/* Notifications Tab - For tent businesses and non-tent businesses */}
              {(isTentBusiness(selectedBusiness) || !isTentBusiness(selectedBusiness)) && (
                <Tab 
                  label={
                    unreadNotificationCount > 0 
                      ? `Notifications (${unreadNotificationCount})` 
                      : "Notifications"
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('🟡 Notifications Tab onClick fired directly!');
                    console.log('🟡 Current activeTab:', activeTab);
                    console.log('🟡 selectedBusiness:', selectedBusiness?.businessName);
                    console.log('🟡 isTentBusiness:', isTentBusiness(selectedBusiness));
                    // Calculate correct tab index based on business type
                    const notificationsTabIndex = getTabIndexForBusiness('Notifications', selectedBusiness);
                    if (notificationsTabIndex === -1) {
                      return;
                    }
                    console.log('🟡 Setting activeTab to:', notificationsTabIndex);
                    setActiveTab(notificationsTabIndex);
                    fetchNotificationCount();
                    fetchVendorNotifications();
                  }}
                />
              )}
              {exploreTabIndex >= 0 && (
                <Tab 
                  label="Explore"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    console.log('🟡 Explore Tab onClick fired directly!');
                    console.log('🟡 Current activeTab:', activeTab);
                    console.log('🟡 selectedBusiness:', selectedBusiness?.businessName);
                    console.log('🟡 Navigating to /explore immediately...');
                    navigate('/explore');
                  }}
                />
              )}
            </Tabs>
          </Box>

          {/* Tab Content */}
          {activeTab === 0 && (
            <Box>
          <ChaaviVendorOverviewHome
            businesses={businesses}
            selectedBusiness={selectedBusiness}
            orders={orders}
            unreadChatCount={unreadChatCount}
            vendorNotifications={vendorNotifications}
            ratingAvg={ratingAgg && ratingAgg.count > 0 ? ratingAgg.avg : null}
            ratingCount={ratingAgg?.count ?? 0}
            onSeeAllBusinesses={() => {
              setChaaviNav('business');
              window.setTimeout(() => {
                document.getElementById('vendor-business-selector')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 80);
            }}
            onEditBusiness={() => setBusinessFormOpen(true)}
            onManageInventory={() => {
              if (!selectedBusiness) return;
              if (isTentBusiness(selectedBusiness) || (!isCateringBusiness(selectedBusiness) && !isFarmhouseBusiness(selectedBusiness))) {
                const idx = getTabIndexForBusiness('Inventory', selectedBusiness);
                if (idx >= 0) setActiveTab(idx);
              } else if (isCateringBusiness(selectedBusiness)) {
                const idx = getTabIndexForBusiness('Plate', selectedBusiness);
                if (idx >= 0) setActiveTab(idx);
              } else {
                const idx = getTabIndexForBusiness('Themes', selectedBusiness);
                if (idx >= 0) setActiveTab(idx);
              }
            }}
            onViewDetails={() => setBusinessFormOpen(true)}
            onMessages={() => navigate('/vendor-chat')}
            onAvailability={() => navigate('/availability')}
            onReviews={() => handleChaaviNav('reviews')}
          />

          <Grid id="vendor-business-details" container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderColor: '#e8e0d4', bgcolor: '#fff' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Typography variant="h6" component="h2">
                  Location & profile
                </Typography>
                <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteDialogOpen(true)}>
                  Delete business
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {selectedBusiness.businessAddress}
              </Typography>
              {selectedBusiness.website && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {selectedBusiness.website}
                </Typography>
              )}
              {selectedBusiness.operatingHours && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Hours: {selectedBusiness.operatingHours}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Minimum Order Amount
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set the minimum order value that clients must meet before placing orders with any of your businesses.
              </Typography>
              
              {/* Display Mode: Show saved value with Edit button */}
              {!isEditingMinOrder && currentMinOrderAmount > 0 && (
                <Box>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: CHA.accentHover }}>
                      {formatCurrency(currentMinOrderAmount)}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<EditIcon sx={{ color: '#fff' }} />}
                      onClick={handleMinOrderEdit}
                      sx={chaaviEditButtonSx}
                    >
                      Edit
                    </Button>
                  </Box>
                  {minOrderMessage && (
                    <Alert
                      severity={minOrderMessage.type}
                      onClose={() => setMinOrderMessage(null)}
                      sx={{ mb: 2 }}
                    >
                      {minOrderMessage.text}
                    </Alert>
                  )}
                </Box>
              )}
              
              {/* Edit Mode: Show input field with Save/Cancel buttons */}
              {(isEditingMinOrder || currentMinOrderAmount === 0) && (
                <Box>
                  <TextField
                    label="Amount"
                    type="number"
                    value={minOrderInput}
                    onChange={(e) => setMinOrderInput(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    error={!isMinOrderInputValid}
                    helperText={
                      !isMinOrderInputValid
                        ? 'Enter a non-negative amount'
                        : currentMinOrderAmount > 0
                        ? `Current: ${formatCurrency(currentMinOrderAmount)}`
                        : 'Enter minimum order amount'
                    }
                    sx={{ maxWidth: 320, mb: 2 }}
                  />
                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <Button
                      variant="contained"
                      onClick={handleMinOrderSave}
                      disabled={!canSaveMinOrder}
                      sx={{ minWidth: 120 }}
                    >
                      {savingMinOrder ? 'Saving...' : 'Save'}
                    </Button>
                    {currentMinOrderAmount > 0 && (
                      <Button
                        variant="outlined"
                        onClick={handleMinOrderCancel}
                        disabled={savingMinOrder}
                        sx={{ minWidth: 120 }}
                      >
                        Cancel
                      </Button>
                    )}
                    {minOrderMessage && (
                      <Alert
                        severity={minOrderMessage.type}
                        onClose={() => setMinOrderMessage(null)}
                        sx={{ flexGrow: 1, minWidth: 200 }}
                      >
                        {minOrderMessage.text}
                      </Alert>
                    )}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

      </Grid>

          {/* Themes Section - Only show for non-catering businesses (including tent) */}
          {(isTentBusiness(selectedBusiness) || selectedBusiness.businessCategory !== 'caters') && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" gutterBottom>
                My Themes ({themes.length})
              </Typography>
              
              {themes.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <PaletteIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No themes created yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start by creating your first theme to showcase your work.
                  </Typography>
                </Paper>
              ) : (
                <Grid container spacing={2}>
                  {themes.map((theme) => (
                    <Grid item xs={12} sm={6} md={4} key={theme.themeId}>
                      <ThemeCard
                        theme={theme}
                        business={selectedBusiness}
                        onEdit={(theme) => {
                          // Navigate to Theme Management tab or open edit form
                          setActiveTab(1);
                          // ThemeManagement component will handle the edit
                        }}
                        onDelete={async (theme) => {
                          if (!window.confirm(`Are you sure you want to delete "${theme.themeName}"?`)) {
                            return;
                          }
                          try {
                            await ThemeService.deleteTheme(theme.themeId, user?.phoneNumber);
                            setThemes(prev => prev.filter(t => t.themeId !== theme.themeId));
                          } catch (err: any) {
                            console.error('Error deleting theme:', err);
                            alert(err.message || 'Failed to delete theme');
                          }
                        }}
                        onViewImages={(theme) => {
                          // Navigate to Theme Management tab
                          setActiveTab(1);
                        }}
                        showActions={true}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Inventory Section - Only show for non-catering businesses (excluding farmhouse) */}
          {(isTentBusiness(selectedBusiness) || (!isCateringBusiness(selectedBusiness) && !isFarmhouseBusiness(selectedBusiness))) && (
            <Box sx={{ mt: 4 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" gutterBottom>
                  My Inventory ({inventory.length})
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setInventoryFormOpen(true)}
                  sx={addInventoryButtonSx}
                  startIcon={<AddIcon sx={{ color: '#fff' }} />}
                >
                  Add Inventory Item
                </Button>
              </Box>
              
              {inventory.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No inventory items yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start by adding your first inventory item to showcase what you offer.
                  </Typography>
                </Paper>
              ) : (
                <Grid container spacing={2}>
                  {inventory.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.inventoryId}>
                      <InventoryCard
                        inventory={item}
                        onEdit={handleEditInventory}
                        onDelete={handleDeleteInventory}
                        onViewImages={handleViewInventoryImages}
                        onPriceUpdate={handlePriceUpdate}
                        showActions={true}
                        refreshTrigger={inventoryRefreshTrigger}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Plates Section - Only show for catering businesses (not tent) */}
          {!isTentBusiness(selectedBusiness) && selectedBusiness.businessCategory === 'caters' && (
            <Box sx={{ mt: 4 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" gutterBottom>
                  My Plates ({plates.length})
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => {
                    setEditingPlate(null);
                    setPlateFormOpen(true);
                  }}
                  sx={chaaviEditButtonSx}
                >
                  Add Plate
                </Button>
              </Box>
              
              {plates.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No plates created yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start by creating your first plate to showcase your catering dishes.
                  </Typography>
                </Paper>
              ) : (
                <Grid container spacing={2}>
                  {plates.map((plate) => (
                    <Grid item xs={12} sm={6} md={4} key={plate.plateId}>
                      <PlateCard
                        plate={plate}
                        business={selectedBusiness}
                        onEdit={handleEditPlate}
                        onDelete={handlePlateDelete}
                        onUpdate={handlePlateUpdate}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Dishes Section - Only show for catering businesses (not tent) */}
          {!isTentBusiness(selectedBusiness) && selectedBusiness.businessCategory === 'caters' && (
            <Box sx={{ mt: 4 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" gutterBottom>
                  My Dishes ({dishes.length})
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleAddDish}
                  sx={chaaviEditButtonSx}
                  startIcon={<AddIcon sx={{ color: '#fff' }} />}
                >
                  Add Dish
                </Button>
              </Box>
              
              {dishes.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No dishes created yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start by creating your first dish to showcase your catering menu.
                  </Typography>
                </Paper>
              ) : (
                <Grid container spacing={2}>
                  {dishes.map((dish) => (
                    <Grid item xs={12} sm={6} md={4} key={dish.dishId}>
                      <DishCard
                        dish={dish}
                        business={selectedBusiness}
                        onEdit={handleEditDish}
                        onDelete={handleDishDelete}
                        onUpdate={handleDishUpdate}
                        hideImageIndicator={true}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}
            </Box>
          )}

          {/* Theme Tab - Show for tent businesses and non-catering businesses only */}
          {activeTab === 1 && selectedBusiness && 
            !isTentBusiness(selectedBusiness) && 
            selectedBusiness.businessCategory !== 'caters' && (
            <ThemeManagement
              themes={themes}
              businessId={selectedBusiness.businessId}
              businessCategory={selectedBusiness.businessCategory}
              onThemesChange={handleThemesChange}
            />
          )}

          {/* Theme Tab - Show for tent businesses */}
          {activeTab === 1 && selectedBusiness && 
            isTentBusiness(selectedBusiness) && (
            <ThemeManagement
              themes={themes}
              businessId={selectedBusiness.businessId}
              businessCategory={selectedBusiness.businessCategory}
              onThemesChange={handleThemesChange}
            />
          )}

          {/* Inventory Tab - Show for tent businesses */}
          {activeTab === 2 && selectedBusiness && isTentBusiness(selectedBusiness) && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" component="h2">
                  Inventory Management
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setInventoryFormOpen(true)}
                  sx={addInventoryButtonSx}
                  startIcon={<AddIcon sx={{ color: '#fff' }} />}
                >
                  Add Inventory Item
                </Button>
              </Box>

              {inventory.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No inventory items yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Start by adding your first inventory item to showcase what you offer.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setInventoryFormOpen(true)}
                    sx={addInventoryButtonSx}
                    startIcon={<AddIcon sx={{ color: '#fff' }} />}
                  >
                    Add First Inventory Item
                  </Button>
                </Paper>
              ) : (
                <Grid container spacing={3}>
                  {inventory.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.inventoryId}>
                      <InventoryCard
                        inventory={item}
                        onEdit={handleEditInventory}
                        onDelete={handleDeleteInventory}
                        onViewImages={handleViewInventoryImages}
                        onPriceUpdate={handlePriceUpdate}
                        showActions={true}
                        refreshTrigger={inventoryRefreshTrigger}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Explore Tab — index varies by business type; tab usually navigates away immediately */}
          {exploreTabIndex >= 0 && activeTab === exploreTabIndex && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h5" gutterBottom>
                Explore Marketplace
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Browse themes, vendors, catering, and venues the way clients do
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/explore')}
                sx={{ mt: 2 }}
              >
                Go to Explore Page
              </Button>
            </Box>
          )}

          {activeTab === 2 && selectedBusiness && !isTentBusiness(selectedBusiness) && !isCateringBusiness(selectedBusiness) && !isFarmhouseBusiness(selectedBusiness) && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" component="h2">
                  Inventory Management
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setInventoryFormOpen(true)}
                  sx={addInventoryButtonSx}
                  startIcon={<AddIcon sx={{ color: '#fff' }} />}
                >
                  Add Inventory Item
                </Button>
              </Box>

              {inventory.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No inventory items yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Start by adding your first inventory item to showcase what you offer.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setInventoryFormOpen(true)}
                    sx={addInventoryButtonSx}
                    startIcon={<AddIcon sx={{ color: '#fff' }} />}
                  >
                    Add First Inventory Item
                  </Button>
                </Paper>
              ) : (
                <Grid container spacing={3}>
                  {inventory.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.inventoryId}>
                      <InventoryCard
                        inventory={item}
                        onEdit={handleEditInventory}
                        onDelete={handleDeleteInventory}
                        onViewImages={handleViewInventoryImages}
                        onPriceUpdate={handlePriceUpdate}
                        showActions={true}
                        refreshTrigger={inventoryRefreshTrigger}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Plates Tab - Only show for catering businesses (not tent) */}
          {activeTab === 1 && !isTentBusiness(selectedBusiness) && selectedBusiness.businessCategory === 'caters' && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Plate Management
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleAddPlate}
                  sx={chaaviEditButtonSx}
                  startIcon={<AddIcon sx={{ color: '#fff' }} />}
                >
                  Add New Plate
                </Button>
              </Box>

              {plates.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No plates yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Start by adding your first plate to showcase your catering dishes.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleAddPlate}
                    sx={{ ...chaaviEditButtonSx, mt: 2 }}
                    startIcon={<AddIcon sx={{ color: '#fff' }} />}
                  >
                    Add First Plate
                  </Button>
                </Paper>
              ) : (
                <Grid container spacing={3}>
                  {plates.map((plate) => (
                    <Grid item xs={12} sm={6} md={4} key={plate.plateId}>
                      <PlateCard
                        plate={plate}
                        business={selectedBusiness}
                        onEdit={handleEditPlate}
                        onDelete={handlePlateDelete}
                        onUpdate={handlePlateUpdate}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Dishes Tab - Only show for catering businesses (not tent) */}
          {activeTab === 2 && !isTentBusiness(selectedBusiness) && selectedBusiness.businessCategory === 'caters' && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Dish Management
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleAddDish}
                  sx={chaaviEditButtonSx}
                  startIcon={<AddIcon sx={{ color: '#fff' }} />}
                >
                  Add New Dish
                </Button>
              </Box>

              {dishes.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No dishes yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Start by adding your first dish to showcase your catering menu.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleAddDish}
                    sx={{ ...chaaviEditButtonSx, mt: 2 }}
                    startIcon={<AddIcon sx={{ color: '#fff' }} />}
                  >
                    Add First Dish
                  </Button>
                </Paper>
              ) : (
                <Grid container spacing={3}>
                  {dishes.map((dish) => (
                    <Grid item xs={12} sm={6} md={4} key={dish.dishId}>
                      <DishCard
                        dish={dish}
                        business={selectedBusiness}
                        onEdit={handleEditDish}
                        onDelete={handleDishDelete}
                        onUpdate={handleDishUpdate}
                        hideImageIndicator={true}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Orders Tab */}
          {selectedBusiness && activeTab === getTabIndexForBusiness('Orders', selectedBusiness) && (
            <Box sx={{ width: '100%', p: 2 }}>
              <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                Orders for {selectedBusiness.businessName}
              </Typography>
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="body1" color="text.secondary">
                  Manage and track all orders from your customers
                </Typography>
                <Button
                  variant="contained"
                  onClick={fetchOrdersForBusiness}
                  disabled={ordersLoading}
                  sx={chaaviEditButtonSx}
                >
                  Refresh Orders
                </Button>
              </Box>

              {ordersError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOrdersError(null)}>
                  {ordersError}
                </Alert>
              )}

              {ordersLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                </Box>
              ) : orders.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No orders yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Orders from customers will appear here once they place them.
                  </Typography>
                </Paper>
              ) : (
                <Grid container spacing={3}>
                  {orders.map((order) => (
                    <Grid item xs={12} key={order.orderId} id={`order-${order.orderId}`}>
                      <Card>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Box>
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Typography variant="h6">
                                  {getOrderDisplayTitle(order)}
                                </Typography>
                                {/* Show NEW indicator for recent orders (within last 24 hours) */}
                                {new Date(order.orderDate).getTime() > Date.now() - 24 * 60 * 60 * 1000 && (
                                  <Chip 
                                    label="NEW" 
                                    size="small"
                                    sx={{ fontWeight: 'bold', bgcolor: CHA.accentHover, color: '#fff' }}
                                  />
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                Customer: {order.customerName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Email: {order.customerEmail}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Phone: {order.customerPhone}
                              </Typography>
                            </Box>
                            <Box textAlign="right">
                              <Chip 
                                label={order.status} 
                                color={
                                  order.status === 'PENDING' ? 'warning' :
                                  order.status === 'CONFIRMED' ? 'info' :
                                  order.status === 'PREPARING' ? 'default' :
                                  order.status === 'READY' ? 'success' :
                                  order.status === 'DELIVERED' ? 'success' :
                                  'error'
                                }
                                sx={{
                                  mb: 1,
                                  ...(order.status === 'PREPARING'
                                    ? {
                                        bgcolor: 'rgba(168, 112, 48, 0.14)',
                                        color: CHA.accentHover,
                                        border: `1px solid ${CHA.accentHover}`,
                                      }
                                    : {}),
                                }}
                              />
                              <Typography variant="h6" sx={{ color: CHA.accentHover }}>
                                ₹{order.totalAmount}
                              </Typography>
                            </Box>
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          <Typography variant="subtitle2" gutterBottom>
                            Order Items:
                          </Typography>
                          {order.orderItems.map((item, index) => (
                            <Box key={index} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <Box>
                                <Typography variant="body2">
                                  {item.itemName} ({item.itemType})
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Qty: {item.quantity} × ₹{item.itemPrice}
                                </Typography>
                              </Box>
                              <Typography variant="body2" fontWeight="bold">
                                ₹{item.itemPrice * item.quantity}
                              </Typography>
                            </Box>
                          ))}

                          <Divider sx={{ my: 2 }} />

                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Order Date: {new Date(order.orderDate).toLocaleString()}
                              </Typography>
                              {order.deliveryDate && (
                                <Typography variant="body2" color="text.secondary">
                                  Delivery Date: {new Date(order.deliveryDate).toLocaleDateString()}
                                </Typography>
                              )}
                              {order.specialNotes && (
                                <Typography variant="body2" color="text.secondary">
                                  Notes: {order.specialNotes}
                                </Typography>
                              )}
                            </Box>
                            <Box display="flex" gap={1}>
                              {order.status === 'PENDING' && (
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={() => handleConfirmOrderClick(order)}
                                >
                                  Confirm Order
                                </Button>
                              )}
                              {order.status === 'CONFIRMED' && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  sx={{ bgcolor: CHA.accentHover, color: '#fff', '&:hover': { bgcolor: CHA.accent } }}
                                  onClick={() => handleOrderStatusUpdate(order.orderId.toString(), 'PREPARING')}
                                >
                                  Start Preparing
                                </Button>
                              )}
                              {order.status === 'PREPARING' && (
                                <Button
                                  variant="contained"
                                  color="info"
                                  size="small"
                                  onClick={() => handleOrderStatusUpdate(order.orderId.toString(), 'READY')}
                                >
                                  Mark Ready
                                </Button>
                              )}
                              {order.status === 'READY' && (
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={() => handleOrderStatusUpdate(order.orderId.toString(), 'DELIVERED')}
                                >
                                  Mark Delivered
                                </Button>
                              )}
                              {order.status !== 'DELIVERED' && order.status !== 'CANCELLED' && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => handleOrderStatusUpdate(order.orderId.toString(), 'CANCELLED')}
                                >
                                  Cancel
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Notifications Tab - Only Notifications */}
          {selectedBusiness && activeTab === getTabIndexForBusiness('Notifications', selectedBusiness) && (
            <Box sx={{ width: '100%', p: 2 }}>
              <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                Notifications
              </Typography>
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="body1" color="text.secondary">
                  {unreadNotificationCount > 0 
                    ? `${unreadNotificationCount} unread notifications`
                    : 'View and manage your notifications'
                  }
                </Typography>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={fetchVendorNotifications}
                    disabled={loadingNotifications}
                    sx={chaaviEditButtonSx}
                  >
                    Refresh
                  </Button>
                  {unreadNotificationCount > 0 && (
                    <Button 
                      variant="contained" 
                      size="small"
                      sx={{ bgcolor: CHA.accentHover, color: '#fff', '&:hover': { bgcolor: CHA.accent } }}
                      onClick={async () => {
                        if (user?.phoneNumber) {
                          try {
                            await notificationService.markAllAsRead(user.phoneNumber);
                            fetchNotificationCount();
                            fetchVendorNotifications();
                          } catch (err) {
                            console.error('Error marking all notifications as read:', err);
                          }
                        }
                      }}
                    >
                      Mark All Read
                    </Button>
                  )}
                </Box>
              </Box>
              
              {loadingNotifications ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                </Box>
              ) : vendorNotifications.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <NotificationIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No notifications yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    You'll receive notifications here when customers place orders or when there are updates.
                  </Typography>
                </Paper>
              ) : (
                <Box>
                  {vendorNotifications.map((notification) => (
                    <Card 
                      key={notification.notificationId} 
                      sx={{ 
                        mb: 2,
                        borderLeft: !notification.isRead ? `4px solid ${CHA.accentHover}` : 'none',
                        bgcolor: !notification.isRead ? 'action.hover' : 'inherit',
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 2,
                        }
                      }}
                      onClick={() => {
                        if (!notification.isRead) {
                          handleMarkNotificationAsRead(notification.notificationId);
                        }
                      }}
                    >
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <NotificationIcon 
                                sx={{ 
                                  fontSize: 20, 
                                  color: notification.isRead ? 'text.secondary' : CHA.accentHover,
                                }} 
                              />
                              <Typography 
                                variant="body1" 
                                sx={{ fontWeight: notification.isRead ? 'normal' : 'bold' }}
                              >
                                {notification.message}
                              </Typography>
                              {!notification.isRead && (
                                <Chip
                                  label="New"
                                  size="small"
                                  sx={{ bgcolor: CHA.accentHover, color: '#fff', fontWeight: 600 }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {new Date(notification.createdAt).toLocaleString()}
                            </Typography>
                            {notification.orderId && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {notification.businessName}
                              </Typography>
                            )}
                          </Box>
                          {!notification.isRead && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkNotificationAsRead(notification.notificationId);
                              }}
                            >
                              Mark Read
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}

        </>
      )}

      {/* Business Management Form */}
      <BusinessManagementForm
        open={businessFormOpen}
        onClose={() => setBusinessFormOpen(false)}
        business={selectedBusiness}
        onSuccess={handleBusinessSuccess}
      />

      {/* Inventory Management Form */}
      {selectedBusiness && (
        <InventoryManagementForm
          open={inventoryFormOpen}
          onClose={() => {
            setInventoryFormOpen(false);
            setEditingInventory(null);
          }}
          inventory={editingInventory}
          businessId={selectedBusiness.businessId}
          onSuccess={handleInventorySuccess}
        />
      )}

      {/* Inventory Images Dialog */}
      <InventoryImages
        open={inventoryImagesOpen}
        onClose={handleInventoryImagesClose}
        inventory={selectedInventory}
      />

      {/* Delete Business Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deletingBusiness && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Business</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{selectedBusiness?.businessName}</strong>?
            <br /><br />
            This action will permanently delete:
            <ul>
              <li>The business profile</li>
              <li>All themes and their images</li>
              <li>All inventory items and their images</li>
              <li>All plates and their images</li>
              <li>All dishes</li>
            </ul>
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={deletingBusiness}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteBusiness} 
            color="error" 
            variant="contained"
            disabled={deletingBusiness}
            startIcon={deletingBusiness ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deletingBusiness ? 'Deleting...' : 'Delete Business'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plate Management Form */}
      {selectedBusiness && (
        <PlateManagementForm
          open={plateFormOpen}
          onClose={() => {
            setPlateFormOpen(false);
            setEditingPlate(null);
          }}
          plate={editingPlate}
          businessId={selectedBusiness.businessId}
          onSuccess={handlePlateSuccess}
        />
      )}

      {/* Dish Management Form */}
      {selectedBusiness && (
        <DishManagementForm
          open={dishFormOpen}
          onClose={() => {
            setDishFormOpen(false);
            setEditingDish(null);
          }}
          dish={editingDish}
          businessId={selectedBusiness.businessId}
          onSuccess={handleDishSuccess}
        />
      )}

      {/* Adjust Order Amount Dialog */}
      {selectedOrderForAdjustment && (
        <AdjustOrderAmountDialog
          open={adjustAmountDialogOpen}
          orderId={selectedOrderForAdjustment.orderId.toString()}
          currentAmount={selectedOrderForAdjustment.totalAmount}
          onConfirm={handleAdjustAmountConfirm}
          onCancel={handleAdjustAmountCancel}
          loading={isUpdatingAmount}
        />
      )}

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ChaaviVendorDashboardShell>
  );
};

export default VendorDashboard;
